import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormContextPlus } from '../../../../form-plus/use-form-context-plus';
import {
  MaskOverlayHeader,
  MaskOverlayStandardSubmitButton,
} from '../../../components';
import { MaskOverlayDeleteButton } from '../../../components/MaskOverlayDeleteButton';
import { MaskOverlayFullscreenToggleButton } from '../../../components/MaskOverlayFullscreenToggleButton';
import { useMaskContext } from '../../../hooks/mask-context';
import { useStandardDeleteEntityHandler } from '../../../hooks/use-standard-delete-entity-handler';
import { ContactTabs } from '../ContactTabs';
import { ContactMaskFormValue } from '../types';

export const ContactsControllerHeader = (props: {
  onDelete: () => void;
  shouldRenderIndividualTab: boolean;
  entityRights: {
    delete: boolean;
  };
}) => {
  const { entityRights, shouldRenderIndividualTab } = props;
  const mask = useMaskContext();
  const { t } = useTranslation();

  const handleDeleteEntitiesClick = useStandardDeleteEntityHandler(
    mask,
    props.onDelete
  );
  const { watch } = useFormContextPlus<ContactMaskFormValue>();

  const [contactFullName, setContactFullName] = useState<string>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSet = useCallback(
    debounce((value: string) => {
      setContactFullName(value);
    }, 300),
    []
  );

  const prevValue = useRef<ContactMaskFormValue>(null);
  useEffect(() => {
    const subscription = watch((value) => {
      if (
        !isEqual(prevValue.current?.firstName, value.firstName) ||
        !isEqual(prevValue.current?.name, value.name)
      ) {
        const fullName = `${value.firstName || ''} ${value.name || ''}`.trim();
        debounceSet(fullName);
      }

      prevValue.current = value;
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [debounceSet, watch]);

  return (
    <MaskOverlayHeader
      title={t(`COMMON.CONTACT`)}
      subTitle={contactFullName}
      actions={
        <>
          <MaskOverlayStandardSubmitButton />
          <MaskOverlayFullscreenToggleButton />
          {mask.isEditMode && (
            <MaskOverlayDeleteButton
              disabled={!entityRights.delete || mask.wip}
              onClick={handleDeleteEntitiesClick}
            />
          )}
        </>
      }
      tabs={
        <ContactTabs
          renderIndividualTab={shouldRenderIndividualTab}
          isCreateMode={mask.isCreateMode}
        />
      }
    />
  );
};
